import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Car, 
  ArrowLeft, 
  Key,
  Building2,
  User,
  Phone,
  Mail,
  CreditCard,
  CheckCircle2,
  Loader2,
  Lock
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthPage = ({ onLogin }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState("key"); // key, register, payment
  const [activeTab, setActiveTab] = useState("register");
  
  // Form state
  const [inviteKey, setInviteKey] = useState("");
  const [isVipKey, setIsVipKey] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [azienda, setAzienda] = useState("");
  const [referente, setReferente] = useState("");
  const [telefono, setTelefono] = useState("");
  
  // Login state
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");

  // Verify invite key
  const handleVerifyKey = async () => {
    if (!inviteKey.trim()) {
      toast.error("Inserisci la chiave di invito");
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/verify-key`, { invite_key: inviteKey });
      toast.success("Chiave valida!");
      setIsVipKey(response.data.is_vip || false);
      setStep("register");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Chiave non valida");
    } finally {
      setLoading(false);
    }
  };

  // Register user
  const handleRegister = async () => {
    if (!email || !password || !azienda || !referente || !telefono) {
      toast.error("Compila tutti i campi");
      return;
    }
    
    if (password.length < 6) {
      toast.error("La password deve avere almeno 6 caratteri");
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/register`, {
        invite_key: inviteKey,
        email,
        password,
        azienda,
        referente,
        telefono
      });
      
      // Se è VIP, accesso diretto senza pagamento
      if (response.data.is_vip) {
        toast.success("Registrazione completata! Accesso VIP attivato.");
        onLogin({ email, azienda, referente, subscription_status: "active" });
        navigate("/calcola");
      } else {
        toast.success("Registrazione completata! Procedi al pagamento.");
        setStep("payment");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore registrazione");
    } finally {
      setLoading(false);
    }
  };

  // Login existing user
  const handleLogin = async () => {
    if (!loginEmail.trim()) {
      toast.error("Inserisci la tua email");
      return;
    }
    
    if (!loginPassword.trim()) {
      toast.error("Inserisci la password");
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, { 
        email: loginEmail,
        password: loginPassword 
      });
      const userData = response.data.user;
      
      if (userData.subscription_status === "active") {
        onLogin(userData);
        toast.success("Bentornato!");
        navigate("/calcola");
      } else {
        setEmail(loginEmail);
        setStep("payment");
        toast.info("Abbonamento scaduto o non attivo. Procedi al pagamento.");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Utente non trovato");
    } finally {
      setLoading(false);
    }
  };

  // Create payment session
  const handlePayment = async () => {
    const userEmail = email || loginEmail;
    if (!userEmail) {
      toast.error("Email non trovata");
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/payments/create-checkout?email=${encodeURIComponent(userEmail)}`);
      
      // Redirect to Stripe
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore creazione pagamento");
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Header */}
      <header className="glass border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <button 
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Home</span>
            </button>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-slate-900 rounded-lg flex items-center justify-center">
                <Car className="w-5 h-5 text-white" />
              </div>
              <span className="font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
                FringeCalculator
              </span>
            </div>
            <div className="w-20"></div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          {step === "key" && (
            <>
              <CardHeader className="text-center">
                <CardTitle className="text-2xl">Accedi al Calcolatore</CardTitle>
                <CardDescription>
                  Inserisci la tua chiave di invito o accedi con la tua email
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                  <TabsList className="grid w-full grid-cols-2 mb-6">
                    <TabsTrigger value="register">Nuova Registrazione</TabsTrigger>
                    <TabsTrigger value="login">Ho già un account</TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="register" className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="inviteKey">Chiave di Invito</Label>
                      <div className="relative">
                        <Key className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                          id="inviteKey"
                          placeholder="Inserisci la chiave ricevuta"
                          value={inviteKey}
                          onChange={(e) => setInviteKey(e.target.value)}
                          className="pl-10"
                          data-testid="invite-key-input"
                        />
                      </div>
                    </div>
                    <Button 
                      className="w-full bg-blue-600 hover:bg-blue-700"
                      onClick={handleVerifyKey}
                      disabled={loading}
                      data-testid="verify-key-btn"
                    >
                      {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                      Verifica Chiave
                    </Button>
                  </TabsContent>
                  
                  <TabsContent value="login" className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="loginEmail">Email</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                          id="loginEmail"
                          type="email"
                          placeholder="La tua email aziendale"
                          value={loginEmail}
                          onChange={(e) => setLoginEmail(e.target.value)}
                          className="pl-10"
                          data-testid="login-email-input"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="loginPassword">Password</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                          id="loginPassword"
                          type="password"
                          placeholder="La tua password"
                          value={loginPassword}
                          onChange={(e) => setLoginPassword(e.target.value)}
                          className="pl-10"
                          data-testid="login-password-input"
                        />
                      </div>
                    </div>
                    <Button 
                      className="w-full bg-blue-600 hover:bg-blue-700"
                      onClick={handleLogin}
                      disabled={loading}
                      data-testid="login-btn"
                    >
                      {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                      Accedi
                    </Button>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </>
          )}

          {step === "register" && (
            <>
              <CardHeader className="text-center">
                <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="w-6 h-6 text-emerald-600" />
                </div>
                <CardTitle className="text-2xl">Completa il Profilo</CardTitle>
                <CardDescription>
                  Inserisci i dati della tua azienda
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="email@azienda.it"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10"
                      data-testid="email-input"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="password"
                      type="password"
                      placeholder="Minimo 6 caratteri"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-10"
                      data-testid="password-input"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="azienda">Nome Azienda</Label>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="azienda"
                      placeholder="Nome dell'azienda"
                      value={azienda}
                      onChange={(e) => setAzienda(e.target.value)}
                      className="pl-10"
                      data-testid="azienda-input"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="referente">Referente</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="referente"
                      placeholder="Nome e Cognome"
                      value={referente}
                      onChange={(e) => setReferente(e.target.value)}
                      className="pl-10"
                      data-testid="referente-input"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="telefono">Telefono</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="telefono"
                      placeholder="+39 ..."
                      value={telefono}
                      onChange={(e) => setTelefono(e.target.value)}
                      className="pl-10"
                      data-testid="telefono-input"
                    />
                  </div>
                </div>
                
                <Button 
                  className="w-full bg-blue-600 hover:bg-blue-700"
                  onClick={handleRegister}
                  disabled={loading}
                  data-testid="register-btn"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Continua
                </Button>
              </CardContent>
            </>
          )}

          {step === "payment" && (
            <>
              <CardHeader className="text-center">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CreditCard className="w-6 h-6 text-blue-600" />
                </div>
                <CardTitle className="text-2xl">Attiva l'Abbonamento</CardTitle>
                <CardDescription>
                  Accesso completo al calcolatore fringe benefit
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="bg-slate-50 rounded-xl p-6 text-center">
                  <p className="text-slate-600 mb-2">Abbonamento Mensile</p>
                  <p className="text-4xl font-bold text-slate-900">€12<span className="text-lg font-normal text-slate-500">/mese</span></p>
                </div>
                
                <ul className="space-y-3">
                  <li className="flex items-center gap-2 text-sm text-slate-600">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                    Calcolo fringe benefit illimitato
                  </li>
                  <li className="flex items-center gap-2 text-sm text-slate-600">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                    Database ACI 2026 con 4.695 veicoli
                  </li>
                  <li className="flex items-center gap-2 text-sm text-slate-600">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                    Supporto nuovo ordine e riassegnazione
                  </li>
                  <li className="flex items-center gap-2 text-sm text-slate-600">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                    Aggiornamenti tabelle inclusi
                  </li>
                </ul>
                
                <Button 
                  className="w-full bg-blue-600 hover:bg-blue-700 h-12"
                  onClick={handlePayment}
                  disabled={loading}
                  data-testid="payment-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Reindirizzamento...
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-4 h-4 mr-2" />
                      Paga con Carta
                    </>
                  )}
                </Button>
                
                <p className="text-xs text-slate-500 text-center">
                  Pagamento sicuro con Stripe. Puoi disdire in qualsiasi momento.
                </p>
              </CardContent>
            </>
          )}
        </Card>
      </main>
    </div>
  );
};

export default AuthPage;
